// src/game-system/character/calculations.js
import { SKILL_RANKS } from '../skills/skillProgression';
import { SKILLS } from '../skills/skillSystem';

/**
 * Calculate ability score modifier
 * @param {number} score - Ability score value
 * @returns {number} Ability modifier
 */
export function getAbilityModifier(score) {
  return Math.floor((score - 10) / 2);
}

/**
 * Calculate proficiency bonus based on character level
 * @param {number} level - Character level
 * @returns {number} Proficiency bonus
 */
export function getProficiencyBonus(level) {
  return Math.floor(2 + (level - 1) / 4);
}

/**
 * Calculate all derived stats for a character
 * @param {Object} character - Character data object
 * @returns {Object} Updated character object with derived stats
 */
export function calculateDerivedStats(character) {
  if (!character) return {};

  // Calculate ability modifiers
  const strMod = getAbilityModifier(character.strength || 10);
  const dexMod = getAbilityModifier(character.dexterity || 10);
  const conMod = getAbilityModifier(character.constitution || 10);
  const intMod = getAbilityModifier(character.intelligence || 10);
  const wisMod = getAbilityModifier(character.wisdom || 10);
  const chaMod = getAbilityModifier(character.charisma || 10);

  // Base AC calculation (10 + DEX modifier)
  const baseAC = 10 + dexMod;

  // Calculate max health (base + CON modifier per level)
  const conBonus = character.level * conMod;
  const baseHealth = getBaseHealthByClass(character.characterClass, character.level);
  const maxHealth = baseHealth + conBonus;

  // Get proficiency bonus
  const proficiencyBonus = getProficiencyBonus(character.level || 1);

  // Calculate initiative
  const initiative = dexMod;

  // Calculate carrying capacity
  const carryingCapacity = character.strength * 15;

  return {
    ...character,
    abilityModifiers: {
      strength: strMod,
      dexterity: dexMod,
      constitution: conMod,
      intelligence: intMod,
      wisdom: wisMod,
      charisma: chaMod
    },
    armorClass: baseAC,
    maxHealth: Math.max(1, maxHealth),
    initiative,
    proficiencyBonus,
    carryingCapacity
  };
}

/**
 * Get base health by class and level
 * @param {string} characterClass - Character class
 * @param {number} level - Character level
 * @returns {number} Base health amount
 */
export function getBaseHealthByClass(characterClass, level) {
  const baseHealthMap = {
    'warrior': 10,
    'barbarian': 12,
    'fighter': 10,
    'paladin': 10,
    'ranger': 10,
    'rogue': 8,
    'wizard': 6,
    'sorcerer': 6,
    'warlock': 8,
    'cleric': 8,
    'druid': 8,
    'bard': 8,
    'monk': 8
  };

  const baseHealth = baseHealthMap[characterClass.toLowerCase()] || 8;

  // First level gets full hit die, subsequent levels get average
  if (level <= 1) {
    return baseHealth;
  }

  // Average hit die roll is (die size / 2) + 1
  const avgHitDieRoll = Math.floor(baseHealth / 2) + 1;
  return baseHealth + (avgHitDieRoll * (level - 1));
}

/**
 * Calculate skill modifier for a character
 * @param {Object} character - Character data
 * @param {string} skillName - Name of the skill
 * @returns {number} Total skill modifier
 */
export function calculateSkillModifier(character, skillName) {
  if (!character || !SKILLS[skillName]) return 0;

  const skill = SKILLS[skillName];
  const attributeModifier = getAbilityModifier(character[skill.attribute] || 10);

  // Check if character has the skill and get rank modifier
  const characterSkill = character.skills?.find(s => s.skill.name.toLowerCase() === skillName.toLowerCase());
  const rankModifier = characterSkill ? SKILL_RANKS[characterSkill.level]?.modifier || 0 : SKILL_RANKS[0].modifier;

  // Check proficiency
  const isProficient = characterSkill?.level > 0;
  const proficiencyBonus = isProficient ? getProficiencyBonus(character.level || 1) : 0;

  return attributeModifier + rankModifier + proficiencyBonus;
}

/**
 * Get experience needed for next character level
 * @param {number} level - Current character level
 * @returns {number} XP needed for next level
 */
export function getXpForNextLevel(level) {
  const xpLevels = [
    0,       // Level 1 requires 0 XP
    300,     // Level 2 requires 300 XP
    900,     // Level 3 requires 900 XP
    2700,    // Level 4
    6500,    // Level 5
    14000,   // Level 6
    23000,   // Level 7
    34000,   // Level 8
    48000,   // Level 9
    64000,   // Level 10
    85000,   // Level 11
    100000,  // Level 12
    120000,  // Level 13
    140000,  // Level 14
    165000,  // Level 15
    195000,  // Level 16
    225000,  // Level 17
    265000,  // Level 18
    305000,  // Level 19
    355000   // Level 20
  ];

  if (level < 1) return 0;
  if (level >= 20) return -1; // Max level

  return xpLevels[level];
}